import { useCallback, useMemo } from 'react';
import {
  type SitePermission,
  useGetSitePermissionsQuery,
} from 'apollo/generated/client-operations';

export type SitePermissions = {
  /** Whether the site's permissions are still being read; every action is hidden meanwhile. A read that failed, or a site the user cannot read, is settled with no permission. */
  loading: boolean;
  /** Whether the current user holds the permission at this site. */
  can: (permission: SitePermission) => boolean;
};

const NONE: ReadonlySet<SitePermission> = new Set();

/**
 * What the current user may do on the zones surfaces of one site, as the
 * API's own catalog answers it (`Jobsite.sitePermissions`). Screens show an
 * action only while `can` says so; the API checks the same catalog on every
 * request, so this hides, it never grants. While the site changes, or its
 * permissions are still loading, nothing is allowed, so a screen cannot
 * keep showing the previous site's actions.
 */
export const useSitePermissions = (
  siteId: string | undefined,
): SitePermissions => {
  const { data, loading, error } = useGetSitePermissionsQuery({
    variables: { jobsiteId: siteId ?? '' },
    skip: !siteId,
    // The app persists Apollo data across sessions; cached permissions are not
    // evidence that this session may act on the site. Recheck before granting.
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const answeredFor = data?.getJobsite?.jobsiteId;
  // Settled: answered for this site, answered that there is no such site
  // for this user, or failed. Only an answer for this site grants anything.
  const settled =
    answeredFor === siteId ||
    (data !== undefined && !loading && !data.getJobsite) ||
    !!error;
  const permissions = useMemo<ReadonlySet<SitePermission>>(
    () =>
      siteId && answeredFor === siteId && !loading && !error
        ? new Set(data?.getJobsite?.sitePermissions ?? [])
        : NONE,
    [siteId, answeredFor, data, loading, error],
  );
  const can = useCallback(
    (permission: SitePermission) => permissions.has(permission),
    [permissions],
  );
  return { loading: !!siteId && (loading || !settled), can };
};
